import type { ResultInnerDetail } from '@empathybroker/tsclient-play/dist/models/search/result'
import type { HalfDonutChartDataSlice } from '@empathyco/insights'
import type { Result } from '@empathyco/x-types'
import type { ExplainMessages } from '../../../models/i18n/explain.messages'
import { ColorEnum } from '../../../enums'
import { capitalizeSentence } from '../../../filters/capitalize-sentence.filter'
import { CssVariableUtils } from '../../../utils/css-variables.utils'
import { ExplainUtils } from '../../../utils/explain.utils'

type ScoreDetailsRecord = Record<string, ScoreDetailsRecordValue>
interface ScoreDetailsRecordValue {
  value?: number
  details: ScoreDetailsRecord | ScoreDetailsArray
}
type ScoreDetailsArray = ResultInnerDetail[]

export function mapResultToHalfDonutChartData(
  result: Result,
  messages: ExplainMessages,
): Array<HalfDonutChartDataSlice> {
  if (ExplainUtils.isResultPinned(result)) {
    return mapPinnedResultToHalfDonutChartData(messages)
  }

  return mapNonPinnedResultToHalfDonutChartData(result, messages)
}

function mapPinnedResultToHalfDonutChartData(
  messages: ExplainMessages,
): Array<HalfDonutChartDataSlice> {
  return [
    {
      name: messages.productAnalysis.pinnedProductTooltip,
      value: 1000,
      color: CssVariableUtils.getHexColorFromColorName(ColorEnum.YELLOW_50),
    },
  ]
}

function mapNonPinnedResultToHalfDonutChartData(
  result: Result,
  messages: ExplainMessages,
): Array<HalfDonutChartDataSlice> {
  const score = ExplainUtils.getResultScoreCriteria(result)
  const scoreDetails = score.details as unknown as ScoreDetailsRecord

  const slices = mapScoreDetailsRecordToHalfDonutChartData(scoreDetails, messages)

  if (ExplainUtils.isResultBuried(result)) {
    return overrideColorOfSlices(slices, ColorEnum.RED_75)
  }

  return slices
}

function mapScoreDetailsRecordToHalfDonutChartData(
  scoreDetails: ScoreDetailsRecord,
  messages: ExplainMessages,
): Array<HalfDonutChartDataSlice> {
  const scoreDetailIds = Object.keys(scoreDetails).filter(key => key !== 'value')
  return scoreDetailIds
    .map(scoreDetailId =>
      mapScoreDetailsRecordValueToHalfDonutChartDataSlice(
        scoreDetailId,
        scoreDetails[scoreDetailId],
        messages,
      ),
    )
    .filter(slice => slice.value > 0)
}

function mapScoreDetailsRecordValueToHalfDonutChartDataSlice(
  detailId: string,
  scoreDetailsRecordValue: ScoreDetailsRecordValue,
  messages: ExplainMessages,
): HalfDonutChartDataSlice {
  const slice: HalfDonutChartDataSlice = {
    name: getSliceName(detailId, messages),
    value: getSliceValue(scoreDetailsRecordValue),
  }

  const color = getSliceColor(detailId)
  if (color) {
    slice.color = color
  }

  const children: Array<HalfDonutChartDataSlice> = getSliceChildren(
    scoreDetailsRecordValue.details,
    messages,
  )
  if (children.length) {
    slice.children = children
  }

  return slice
}

function getSliceName(detailId: string, messages: ExplainMessages): string {
  return ExplainUtils.getResultScoreCriteriaDetailMessages(detailId, messages.score).label
}

function getSliceValue(
  scoreDetailsRecordValueOrResultInnerDetail: ScoreDetailsRecordValue | ResultInnerDetail,
): number {
  let result = 0
  const value = scoreDetailsRecordValueOrResultInnerDetail.value
  if (value) {
    result = value
  } else {
    if ('details' in scoreDetailsRecordValueOrResultInnerDetail) {
      result = computeSliceValueFromScoreRecordValue(scoreDetailsRecordValueOrResultInnerDetail)
    }
  }
  return Math.log1p(result)
}

function computeSliceValueFromScoreRecordValue(
  scoreDetailsRecordValue: ScoreDetailsRecordValue,
): number {
  if (Array.isArray(scoreDetailsRecordValue.details)) {
    return removeBuriesFromScoreDetailsArray(scoreDetailsRecordValue.details).reduce(
      (sum: number, innerDetail: ResultInnerDetail) => sum + innerDetail.value,
      0,
    )
  }
  return 0
}

function getSliceColor(detailId: string): string {
  const colorName = ExplainUtils.getResultScoreCriteriaDetailColor(detailId)
  if (colorName) {
    return CssVariableUtils.getHexColorFromColorName(colorName)
  }
  return ''
}

function getSliceChildren(
  socreDetailsRecordOrArray: ScoreDetailsRecord | ScoreDetailsArray,
  messages: ExplainMessages,
): Array<HalfDonutChartDataSlice> {
  let result: Array<HalfDonutChartDataSlice> = []
  if (Array.isArray(socreDetailsRecordOrArray)) {
    result = mapScoreDetailsArrayToHalfDonutChartData(socreDetailsRecordOrArray)
  } else {
    result = mapScoreDetailsRecordToHalfDonutChartData(socreDetailsRecordOrArray, messages)
  }
  return result.filter(slice => slice.value > 0)
}

function mapScoreDetailsArrayToHalfDonutChartData(
  scoreDetailsArray: ScoreDetailsArray,
): Array<HalfDonutChartDataSlice> {
  return removeBuriesFromScoreDetailsArray(scoreDetailsArray).map(innerDetail => ({
    name: capitalizeSentence(innerDetail.fieldName),
    value: getSliceValue(innerDetail),
  }))
}

const INNER_DETAIL_TYPES_BURY = 'bury'

function removeBuriesFromScoreDetailsArray(
  scoreDetailsArray: ScoreDetailsArray,
): ScoreDetailsArray {
  return scoreDetailsArray.filter(
    innerDetail => !innerDetail.type || innerDetail.type !== INNER_DETAIL_TYPES_BURY,
  )
}

function overrideColorOfSlices(
  slices: HalfDonutChartDataSlice[],
  color: ColorEnum,
): HalfDonutChartDataSlice[] {
  return slices.map((slice: HalfDonutChartDataSlice) => ({
    ...slice,
    color: CssVariableUtils.getHexColorFromColorName(color),
  }))
}
